import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Card, CardContent, Divider, Typography,
} from '@mui/material';
import { ExpandMore, HelpOutline, TipsAndUpdatesRounded } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { CheckboxWithLabel, MaskedInput, TextInput, Tooltip } from '@uplift/ui-v2';

type PayorEnrollmentFormContentProps = {
  elevated?: boolean;
};

export const PayorEnrollmentFormContent: React.VFC<PayorEnrollmentFormContentProps> = ({ elevated = true }) => {
  const { classes } = useStyles({ elevated });
  const cardElevation = elevated ? 1 : 0;

  return (
    <>
      <Card className={elevated ? classes.elevatedCard : undefined} elevation={cardElevation}>
        <CardContent className={classes.inputsContent}>
          <MaskedInput name="npi" type="npi" label="NPI Number" fullWidth color="secondary" />
          <MaskedInput
            name="caqhId"
            type="caqh"
            label={(
              <Box display="flex" alignItems="center">
                CAQH Number
                <Tooltip title="Your CAQH number uniquely identifies your provider profile." placement="right" arrow>
                  <HelpOutline fontSize="small" sx={{ ml: 0.5, color: 'text.secondary', cursor: 'default' }} />
                </Tooltip>
              </Box>
            )}
            fullWidth
            color="secondary"
          />
          <TextInput name="caqhUsername" label="CAQH Username" fullWidth color="secondary" />
          <TextInput name="caqhPassword" label="CAQH Password" type="password" fullWidth color="secondary" />
        </CardContent>
        <Box className={classes.accordionWrapper}>
          <Accordion elevation={0} className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMore color="primary" />} className={classes.accordionSummary}>
              <Box display="flex" alignItems="center" gap="12px">
                <TipsAndUpdatesRounded color="primary" />
                <Typography variant="body2" color="primary" fontWeight={600}>
                  Why do we ask for your CAQH credentials?
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Box className={classes.accordionBody}>
                <Typography variant="body2" color="textPrimary">
                  To complete your insurance credentialing, we securely access your CAQH ProView account to:
                </Typography>
                <Box component="ul" className={classes.bulletList}>
                  <Typography component="li" variant="body2" color="textPrimary">Add BetterHelp as a participating organization</Typography>
                  <Typography component="li" variant="body2" color="textPrimary">Verify your license, NPI, and other required information</Typography>
                  <Typography component="li" variant="body2" color="textPrimary">Ensure your profile is ready for payer enrollment</Typography>
                </Box>
                <Typography variant="body2" color="textPrimary">
                  We use a HIPAA-compliant credentialing platform (Verifiable) for this process. Your credentials are encrypted and used only once to retrieve this information.
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  <b>Security tip:</b> We recommend changing your CAQH password once credentialed and enrolled.
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Card>
      <Card className={elevated ? classes.elevatedCard : undefined} elevation={cardElevation}>
        <CardContent className={classes.cardContent}>
          <Typography variant="body2" color="textPrimary">
            <b>To submit, please confirm the following:</b>
          </Typography>
          <CheckboxWithLabel
            name="confirmProfile"
            color="secondary"
            label={(
              <Typography variant="body2" color="textPrimary">
                My CAQH profile is complete, accurate, and up to date, including at least 5 years of work history and/or documented gap explanations, all active licenses, and all relevant state release forms.
              </Typography>
            )}
          />
          <CheckboxWithLabel
            name="authorizeAccess"
            color="secondary"
            label={(
              <Typography variant="body2" color="textPrimary">
                I authorize BetterHelp and UpLift Behavioral Health PC to access my CAQH profile and understand I will be contracting with UpLift Behavioral Health PC to be credentialed to perform services.
              </Typography>
            )}
          />
        </CardContent>
        <Divider />
        <CardContent className={classes.highlightSection}>
          <CheckboxWithLabel
            name="confirmAccuracy"
            color="secondary"
            label={(
              <Typography variant="body2" color="textPrimary">
                I have reviewed my CAQH number and credential information above to ensure accuracy. I understand corrections after submission require reprocessing and will delay enrollment.
              </Typography>
            )}
          />
        </CardContent>
      </Card>
    </>
  );
};

const useStyles = makeStyles<{ elevated: boolean }>()((theme, { elevated }) => ({
  elevatedCard: {
    width: '100%',
  },
  inputsContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: theme.spacing(3),
    paddingBottom: 0,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: theme.spacing(3),
    '&:last-of-type': {
      paddingBottom: theme.spacing(3),
    },
  },
  highlightSection: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(3),
    '&:last-of-type': {
      paddingBottom: theme.spacing(3),
    },
  },
  accordionWrapper: {
    padding: elevated ? theme.spacing(3, 3) : theme.spacing(3, 3, 0),
  },
  accordion: {
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  accordionSummary: {
    padding: 0,
    minHeight: '48px',
    '&.Mui-expanded': {
      minHeight: '48px',
    },
  },
  accordionDetails: {
    padding: 0,
    paddingBottom: 0,
  },
  accordionBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  bulletList: {
    paddingLeft: theme.spacing(2.5),
    margin: 0,
  },
}));
